// src/services/CacheService.ts
interface CacheItem<T> {
  value: T;
  expiresAt: number;
}

export class CacheService {
  private static instance: CacheService;
  private cache: Map<string, CacheItem<any>> = new Map();
  private readonly DEFAULT_TTL = 300; // 5 minutes in seconds

  private constructor() {
    // Clean up expired items every minute
    setInterval(() => this.cleanup(), 60000);
  }

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  async set<T>(
    key: string,
    value: T,
    ttl: number = this.DEFAULT_TTL,
  ): Promise<void> {
    try {
      const expiresAt = Date.now() + ttl * 1000;
      this.cache.set(key, { value, expiresAt });

      // Also store in localStorage for persistence
      if (typeof window !== "undefined") {
        localStorage.setItem(
          `cache_${key}`,
          JSON.stringify({ value, expiresAt }),
        );
      }
    } catch (error) {
      console.warn("Cache set failed:", error);
    }
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      // Check memory cache first
      const item = this.cache.get(key);
      if (item && item.expiresAt > Date.now()) {
        return item.value as T;
      }

      // Check localStorage
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem(`cache_${key}`);
        if (stored) {
          const parsed: CacheItem<T> = JSON.parse(stored);
          if (parsed.expiresAt > Date.now()) {
            // Update memory cache
            this.cache.set(key, parsed);
            return parsed.value;
          } else {
            // Remove expired item
            this.delete(key);
          }
        }
      }

      return null;
    } catch (error) {
      console.warn("Cache get failed:", error);
      return null;
    }
  }

  async delete(key: string): Promise<void> {
    this.cache.delete(key);
    if (typeof window !== "undefined") {
      localStorage.removeItem(`cache_${key}`);
    }
  }

  async clear(): Promise<void> {
    this.cache.clear();
    if (typeof window !== "undefined") {
      Object.keys(localStorage)
        .filter((key) => key.startsWith("cache_"))
        .forEach((key) => localStorage.removeItem(key));
    }
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (item.expiresAt <= now) {
        this.cache.delete(key);
      }
    }
  }

  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}
